import React, {Fragment, Key, useLayoutEffect, useState} from "react";
import {Control} from "react-hook-form/dist/types";
import {Controller, ControllerFieldState, FieldPath} from "react-hook-form";
import {FieldValues} from "react-hook-form/dist/types/fields";
import {Combobox, Transition} from "@headlessui/react";
import classNames from "classnames";
import {RegisterOptions} from "react-hook-form/dist/types/validator";
import {LoadingSpinner} from "../../components/comboBox/ComboBox";
import {IconCheck, IconSelector, IconX} from "@tabler/icons-react";

interface MyComboboxProps<T> {
    label?: string,
    labelClassName?: string,
    inlineLabel?: boolean,
    value: T[],
    onChange: (value: T[]) => void,
    data: T[] | undefined,
    isLoading?: boolean,
    searchablePropsName?: (keyof T)[],
    isSameFn: (a: T, b: Partial<T>) => boolean,
    keyValueFn: (v: T) => Key,
    displayValueFn: (v: T) => string,
    disabled?: boolean,
    fieldState: ControllerFieldState,
}

function MyCombobox<T>(props: MyComboboxProps<T>) {
    const {label, labelClassName, isLoading, data, disabled, keyValueFn, displayValueFn, isSameFn, value, onChange, fieldState, inlineLabel = false} = props;

    const propsName: (keyof T)[] = [];
    if (data && data.length > 0) {
        let k: keyof T;
        for (k in data[0]) {
            propsName.push(k);
        }
    }

    const {searchablePropsName = propsName} = props;

    const [selectedElements, setSelectedElements] = useState<T[]>([]);
    // Allineo valore corrente con option corrispondente
    useLayoutEffect(() => {
        const elementi: T[] = [];
        if (value && data && data.length > 0) {
            value.forEach((val) => {
                const elemento = data.find(elemento => isSameFn(elemento, val));
                if (elemento)
                    elementi.push(elemento);
            });
        }
        setSelectedElements(elementi);

    }, [value, data, setSelectedElements, isSameFn])

    const [query, setQuery] = useState<string>('')

    // Compone la stringa in cui cercare il testo indicato dall'utente
    const getSearchContext = (elemento: T) => {
        let str = '';
        if (searchablePropsName)
            for (let prop of searchablePropsName) {
                str = str + elemento[prop] + '|';
            }
        return str.toLowerCase()
            .replace(/\s+/g, "");
    }

    // Compongo array (filtrato) per options
    let filteredData: T[] = [];
    if (data) {
        filteredData =
            (query === '' || query === null)
                ? data
                : data.filter((elemento: T) =>
                    getSearchContext(elemento)
                        .includes(query.toLowerCase().replace(/\s+/g, ""))
                );
    }

    const popElemento = (elemento: T) => {
        //setSelectedPeople((v) => v.filter(obj => keyValueFn(obj) !== keyValueFn(elemento)));
        onChange(selectedElements.filter(obj => keyValueFn(obj) !== keyValueFn(elemento)));
        console.log('pop', keyValueFn(elemento));
    }

    const errorMessage: string | undefined = fieldState.error ? (fieldState.error.message!.length > 0 ? fieldState.error.message : 'Campo obbligatorio.') : undefined;
    const cbLabel = <Combobox.Label className={`block text-sm font-medium ${disabled ? 'opacity-50' : ''} ${labelClassName ? labelClassName : ''}`}>{label && label}</Combobox.Label>;
    const combo = <div className="relative rounded-md border border-gray-300 mt-1">
        {<>
            {selectedElements.length > 0 && (
                <ul className="">
                    {selectedElements.map((elemento) => (
                        <li key={keyValueFn(elemento)} className="inline-flex justify-center border border-gray-300 m-1 pr-1">
                            {/*https://stackoverflow.com/questions/64836034/a-or-button-only-can-be-effect-when-clicking-on-the-top-half-part*/}
                            <button type="button" onClick={() => popElemento(elemento)} className="z-10">
                                <IconX className="h-4 w-4 text-gray-400"/>
                            </button>
                            {displayValueFn(elemento)}
                        </li>
                    ))}
                </ul>
            )}
            <div className="relative">
                <Combobox.Input
                    className={`border-none rounded-md w-full bg-white py-2 pl-3 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm ${disabled ? 'bg-gray-200 cursor-not-allowed' : ''}`}
                    onChange={(event) => setQuery(event.target.value)}
                    /*displayValue={(people) => people ? people.map((person) => displayValueFn(person as T)).join(', ') : ''}*/
                    displayValue={() => ''}
                    placeholder='Scegli un valore...'
                    autoComplete="off"
                    readOnly={!searchablePropsName}
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                    <IconSelector className="h-5 w-5 text-gray-400"/>
                </Combobox.Button>
            </div>
        </>}
        <span className="absolute inset-y-0 left-0 flex items-center px-2">
                    {isLoading && <LoadingSpinner/>}
                        </span>

        <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
        >

            <Combobox.Options
                className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {filteredData.length === 0 ? (
                    <div className="cursor-default select-none relative py-2 px-4 text-red-700">
                        Non ho trovato nulla
                    </div>
                ) : (
                    filteredData.map((elemento) => (
                        <Combobox.Option
                            key={keyValueFn(elemento)}
                            value={elemento}
                            className={({active}) =>
                                classNames(
                                    'relative cursor-default select-none py-2 pl-3 pr-9',
                                    active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                )
                            }
                        >
                            {({active, selected}) => (
                                <>
                                    <span className={classNames('block truncate', selected && 'font-semibold')}>{displayValueFn(elemento)}</span>
                                    {selected && (
                                        <span
                                            className={classNames(
                                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                                active ? 'text-white' : 'text-blue-600'
                                            )}>
                                                        <IconCheck className="h-5 w-5" aria-hidden="true"/>
                                                    </span>
                                    )}
                                </>
                            )}
                        </Combobox.Option>
                    ))
                )}
            </Combobox.Options>

        </Transition>
    </div>;
    return <>
        <Combobox value={selectedElements} onChange={onChange} multiple>

            {inlineLabel && <div className="inline-flex items-center space-x-2">
                {cbLabel}
                {combo}
            </div>}
            {!inlineLabel && <>
                {cbLabel}
                {combo}
            </>}

        </Combobox>
        {errorMessage && <p className="text-rose-700">{errorMessage}</p>}
    </>
}


interface MultiComboBoxProps<T, TFieldValues extends FieldValues> {
    control: Control<TFieldValues>,
    name: FieldPath<TFieldValues>,
    label?: string,
    labelClassName?: string,
    inlineLabel?: boolean,
    data?: T[],
    isLoading?: boolean,
    searchablePropsName?: (keyof T)[],
    isSameFn: (a: T, b: Partial<T>) => boolean,
    keyValueFn: (v: T) => Key,
    factory: (v: any) => Partial<T>,
    displayValueFn: (v: T) => string,
    disabled?: boolean,
    rules?: Omit<RegisterOptions<TFieldValues, FieldPath<TFieldValues>>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>
}

export function MultiComboBox<T, V extends FieldValues>(props: MultiComboBoxProps<T, V>) {
    const {control, name, label, labelClassName, isLoading, data, disabled, keyValueFn, displayValueFn, isSameFn, factory, searchablePropsName, rules, inlineLabel = false} = props;
    const normalizeValue = (value: Key[]) => {
        const elementi: T[] = [];
        if (value && data && data.length > 0) {
            value.forEach((val) => {
                const elemento = data.find(elemento => isSameFn(elemento, factory(val)));
                if (elemento)
                    elementi.push(elemento);
            });
        }
        return elementi;
    }
    //const defaultValue = [] as T[];
    return (
        <Controller name={name}
                    control={control}
            /*defaultValue={defaultValue}*/
                    rules={rules}
                    render={({field: {value, onChange}, fieldState}) => {
                        return (
                            <MyCombobox<T> label={label} labelClassName={labelClassName} isLoading={isLoading} data={data} disabled={disabled} fieldState={fieldState}
                                           searchablePropsName={searchablePropsName}
                                           inlineLabel={inlineLabel}
                                           value={normalizeValue(value)}

                                           onChange={(v) => onChange(v.map((val) => {
                                               return keyValueFn(val)
                                           }))}

                                           displayValueFn={displayValueFn}
                                           keyValueFn={keyValueFn}
                                           isSameFn={isSameFn}
                            />
                        )
                    }}
        />
    )
}

export default MultiComboBox;